import React from 'react';
import { PDFViewer, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { Button } from '@mui/material';
import { saveAs } from 'file-saver';
// amountToRelease / loanAmount / gst / insuranceAmount store PAISE.
import { toRupees } from "../../utils/money";

// Styles for the PDF
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: 'Helvetica',
  },
  header: {
    textAlign: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  section: {
    marginBottom: 10,
  },
  label: {
    fontWeight: 'bold',
    marginBottom: 5,
  },
  text: {
    marginBottom: 5,
  },
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCol: {
    width: '50%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    padding: 5,
  },
  footer: {
    marginTop: 20,
    textAlign: 'center',
  },
  acknowledgment: {
    marginTop: 30,
    marginBottom: 20,
  },
  signature: {
    marginTop: 10,
    marginBottom: 5,
  },
  footer: {
    marginRight: 10,
    textAlign: 'right',
  },
  address: {
    marginTop: 20,
    textAlign: 'center',
  },
});

// Utility to convert numbers to words
const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const thousands = ['', 'Thousand', 'Lakh', 'Crore'];

  const convertLessThanThousand = (n) => {
    if (n === 0) return '';
    if (n < 10) return units[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
      return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + units[n % 10] : '');
    }
    return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convertLessThanThousand(n % 100) : '');
  };

  let result = '';
  let groupIndex = 0;
  while (num > 0) {
    const group = num % 1000;
    if (group > 0) {
      result = convertLessThanThousand(group) + (thousands[groupIndex] ? ' ' + thousands[groupIndex] : '') + (result ? ' ' + result : '');
    }
    num = Math.floor(num / 1000);
    groupIndex++;
  }
  return result.trim();
};

const SanctionLetterView = ({ personalDetailsData, loanDetailsData, proposedLoanDetailsData, memberDetailsData, onBack }) => {
  // Dynamic data from props with fallbacks
  const borrowerName = personalDetailsData?.name || 'N/A';
  const borrowerAddress = personalDetailsData?.permanentStreetAddress || 'N/A';
  // Use ?? not ||: amountToRelease is legitimately 0 until an admin approves
  // the loan, and 0 must not be treated as "no value".
  // PAISE -> RUPEES. amountToRelease was converted by
  // 20260909124252-money-to-paise-bigint-stage2; without this the sanction
  // letter states a figure 100x the loan — on a document handed to the customer.
  const loanAmount = toRupees(memberDetailsData?.amountToRelease ?? 0);
  const loanPurpose = loanDetailsData?.loanPurpose || 'N/A';
  const interestRate = proposedLoanDetailsData?.interestRate || "24";
  const tenure = proposedLoanDetailsData?.tenureInMonths || 0;
  // monthlyEmi is int(11) and was NOT converted — still rupees, left alone.
  const emi = proposedLoanDetailsData?.monthlyEmi || 0;
  // processingFee WAS converted to paise by the same migration.
  const processingFee = toRupees(proposedLoanDetailsData?.processingFee || 0);
  const applicationDate = proposedLoanDetailsData?.emiDate || '-'; // Adjust field name as needed

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD</Text>
          <Text style={styles.title}>SANCTION LETTER FOR JOTHILINGA LOAN</Text>
          <Text>Date: {new Date().toLocaleDateString('en-IN')}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>To,</Text>
          <Text style={styles.text}>{borrowerName}</Text>
          <Text style={styles.text}>{borrowerAddress}</Text>
          <Text style={styles.text}>Dear Sir/Madam,</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>TERMS AND CONDITIONS</Text>
          <Text style={styles.text}>
            With reference to your application for loan, we are pleased to inform you that a loan facility has been sanctioned to you for the amount and purpose as mentioned below.
          </Text>
        </View>

        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Date of Application</Text>
              <Text>{applicationDate}</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Loan Purpose</Text>
              <Text>{loanPurpose}</Text>
            </View>
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Security</Text>
              <Text>-</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Loan Amount Sanctioned</Text>
              <Text>RS. {loanAmount}/- ({numberToWords(loanAmount)} Rupees)</Text>
            </View>
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Processing Fee</Text>
              <Text>RS. {processingFee}/- ({numberToWords(processingFee)} Rupees)</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.label}>GST</Text>
              <Text>RS. 0/-</Text>
            </View>
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Insurance (Including GST)</Text>
              <Text>RS. 0/-</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Net Eligible Disbursement</Text>
              <Text>RS. {loanAmount}/- ({numberToWords(loanAmount)} Rupees)</Text>
            </View>
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Rate Of Interest (Fixed)</Text>
              <Text>{interestRate}%</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Tenure (months)</Text>
              <Text>{tenure}</Text>
            </View>
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Moratorium, if any (months)</Text>
              <Text>Nil</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.label}>EMI</Text>
              <Text>RS. {emi}/- ({numberToWords(emi)} Rupees)</Text>
            </View>
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Repayment Frequency</Text>
              <Text>Month</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Late payment charges</Text>
              <Text>0%</Text>
            </View>
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.label}>Special Conditions, if any</Text>
              <Text>-</Text>
            </View>
            <View style={styles.tableCol}>
              <Text style={styles.label}></Text>
              <Text></Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>ADDITIONAL CONDITIONS</Text>
          <Text style={styles.text}>1. The term loan requires UDC/PDC, from Borrower Guarantee and specifications as mentioned in the terms and conditions above.</Text>
          <Text style={styles.text}>2. The loan will be further secured by a Demand Promissory Note executed by the borrower(s) in favor of JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD.</Text>
          <Text style={styles.text}>3. The loan shall be disbursed subject to satisfactory legal, technical and financial scrutiny, submission of relevant documents along with UDC/PDC cheques or ECS mandate in favour of the Company and any other condition stipulated by the Company.</Text>
          <Text style={styles.text}>4. Applicant/s will maintain the saving bank account with any scheduled bank and, if required by the Company, give debit instructions or cheques towards repayment of Loan and shall maintain sufficient balance to ensure there is no default on EMI. The said account shall not be closed till complete repayment of all amount, charges, expenses, fees etc. payable to the Company.</Text>
          <Text style={styles.text}>5. The loan amount shall be utilized strictly for the purposes specified in the application and as prescribed by the Company.</Text>
          <Text style={styles.text}>6. The borrower will be charged a fixed rate of interest as mentioned in the terms and conditions. In case the interest rate is not changed the prevailing interest rate will automatically continue. The Company shall have the sole discretion to determine such changes in the rate of interest.</Text>
          <Text style={styles.text}>7. All legal and other expenses if any, like lawyer's fees, insurance premium, stamp duty registration charges and other incidental expenses incurred in connection with the loan shall be borne by the borrower.</Text>
          <Text style={styles.text}>8. In case the borrower/s fail(s) to utilize the disbursed amount, the Company may ask borrower to return the loan amount, however Processing Fee, Insurance Premium and other charges will not be refunded. The Company as its option may recalibrate the loan tenor by keeping the EMI same on a Sanctioned amount or by keeping the same tenor with changed EMI.</Text>
          <Text style={styles.text}>9. In case of default, the loan amount can be recalled at the sole discretion of the Company, and additional penal interest of 2% will be charged for the period of delay on the overdue amount. The charges for sending dunning letters, reminder letter, recall notices and any other charges on legal proceedings will be charged to borrower/s loan account including lawyer fees and court fees.</Text>
          <Text style={styles.text}>10. The loan shall be utilized for the purpose for which it is sanctioned and shall not be utilized for capital market exposure/ onward lending against securities / margins and other activities specifically prohibited by Reserve Bank of India.</Text>
          <Text style={styles.text}>11. In the token of acknowledgement of the contents having been read out and explained, please return to us the duplicate copy signed.</Text>
          <Text style={styles.text}>12. Pre EMI will be applicable for cases where the first EMI date exceeds 30 days from the date of disbursement.</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.text}>We wish you a happy future with the financial assistance of JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD.</Text>
        </View>

        <View style={styles.footer}>
          <Text>Yours faithfully,</Text>
          <Text>Authorised Signatory</Text>
          {/* <Text style={{ marginTop: 20 }}>Registered & Corporate Office: No.3/1799, NHst, Akkalmadam North Road, Pamban-623521</Text>
          <Text>Ph: 8807201694 | E-Mail: jtfpcl2@gmail.com</Text> */}
        </View>

        <View style={styles.acknowledgment}>
          <Text style={styles.label}>ACKNOWLEDGEMENT</Text>
          <Text style={styles.text}>Received the original. The above terms and condition have been read out and explained to us to our satisfaction and are acceptable to us.</Text>
          <Text style={styles.signature}>Signature Of Borrower: ____________________</Text>
          <Text style={styles.signature}>Signature Of Co-Borrower 1: ____________________</Text>
          <Text style={styles.signature}>Signature Of Co-Borrower 2: ____________________</Text>
        </View>

        <View style={styles.address}>
          <Text>Office Address: 120-A, Railway Feeder Road, Market Street, Rameswaram - 623526, Ramanathapuram District, Tamil Nadu.</Text>
          <Text>Ph: 9788842197, 6380583780 | E-Mail: jlnidhiltd@gmail.com</Text>
        </View>
      </Page>
    </Document>
  );

  const handleDownload = async () => {
    // Format borrower name for filename - replace spaces with underscores and remove special characters
    const sanitizedName = borrowerName.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_');
    // Create a descriptive filename with borrower name
    const fileName = `${sanitizedName}_Sanction_Letter.pdf`;

    const pdfBlob = await import('@react-pdf/renderer').then(({ pdf }) =>
      pdf(<MyDocument />).toBlob()
    );
    saveAs(pdfBlob, fileName);
  };

  return (
    <div>
      <Button variant="contained" onClick={onBack} style={{ marginBottom: '10px' }}>
        Back
      </Button>
      <Button variant="contained" onClick={handleDownload} style={{ marginBottom: '10px', marginLeft: '10px' }}>
        Download PDF
      </Button>
      <PDFViewer width="100%" height="600px">
        <MyDocument />
      </PDFViewer>
    </div>
  );
};

export default SanctionLetterView;